// ChatInterface.js - Version avec affichage systématique des boutons de sélection de mode

import React, { useState, useEffect, useRef, useCallback } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { auth } from '../firebase';
import {
  Bot, User, Send, Mic, MicOff, Maximize2, Minimize2, Target, Globe, MessageCircle,
  RefreshCw, AlertCircle, CheckCircle, Clock, Sparkles, Star, Zap,
  Volume2, VolumeX, Loader, History, Eye, EyeOff, HelpCircle,
  ChevronDown, ChevronUp, X, PenTool, FileText
} from 'lucide-react';

import { useChatConversation } from './ChatConversation';
import { 
  useChatGuided, 
  GuidedModeToggle,
  ContextualQuestionsPanel,
  FeedbackMessage 
} from './ChatGuided';

import EnhancedChatMarkdown from './ChatMarkdown';
import SaveToKnowledgeBase from './SaveToKnowledgeBase';
import ToolModeDropdown from './ToolModeDropdown';
import SourcesPanel from './SourcesPanel';
import RelatedQuestionsPanel from './RelatedQuestionsPanel';
import KnowledgeBase from '../CP-KnowledgeDB/knowledgeBase';
import InspirationChatView from './InspirationChatView';

const fontStyles = `
  .chat-title { font-size: 1.75rem; line-height: 2.25rem; font-weight: 700; }
  .section-subtitle { font-size: 1.25rem; line-height: 1.875rem; font-weight: 600; }
  .chat-text { font-size: 1.125rem; line-height: 1.75rem; font-weight: 400; }
  .input-text { font-size: 1.125rem !important; line-height: 1.625rem !important; }
  
  @media (max-width: 768px) {
    .chat-title { font-size: 1.5rem; line-height: 2rem; }
    .section-subtitle { font-size: 1.125rem; line-height: 1.75rem; }
    .chat-text { font-size: 1rem; line-height: 1.5rem; }
    .input-text { font-size: 1rem !important; line-height: 1.5rem !important; }
  }
  
  .animate-slide-in-right {
    animation: slideInRight 0.3s ease-out;
  }
  
  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

// Composant champ de saisie
const ChatInputField = ({ 
  inputText, 
  setInputText, 
  handleSendMessage, 
  handleKeyPress, 
  toggleListening, 
  isListening, 
  isLoading, 
  browserSupportsSpeechRecognition, 
  placeholder,
  chatMode = 'conversation'
}) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [inputText]);

  return (
    <div className="flex gap-2 md:gap-3 items-end bg-white rounded-xl border border-gray-300 p-3 md:p-4 shadow-sm">
      <button
        onClick={toggleListening}
        className={`p-2 md:p-3 rounded-xl transition-all duration-200 flex-shrink-0 shadow-sm ${
          isListening 
            ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white animate-pulse shadow-md' 
            : 'bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-50'
        }`}
        disabled={isLoading || !browserSupportsSpeechRecognition}
        title={isListening ? 'Arrêter l\'écoute' : 'Reconnaissance vocale'}
      >
        {isListening ? <MicOff size={16} className="md:w-5 md:h-5" /> : <Mic size={16} className="md:w-5 md:h-5" />}
      </button>
      
      <div className="flex-1 relative">
        <textarea
          ref={textareaRef}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          className="w-full p-2 md:p-3 border-0 focus:ring-0 focus:outline-none resize-none input-text bg-transparent"
          rows={1}
          disabled={isLoading}
          style={{ minHeight: '24px', maxHeight: '120px', lineHeight: '1.5' }}
        />
      </div>
      
      <button
        onClick={handleSendMessage}
        disabled={!inputText.trim() || isLoading}
        className={`p-2 md:p-3 rounded-xl transition-all duration-200 flex-shrink-0 shadow-sm ${
          inputText.trim() && !isLoading
            ? chatMode === 'guided'
              ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 shadow-md'
              : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-md'
            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
        }`}
      >
        {isLoading ? <Loader size={16} className="animate-spin md:w-5 md:h-5" /> : <Send size={16} className="md:w-5 md:h-5" />}
      </button>
    </div>
  );
};

// Composant bulle de message avec sauvegarde
const MessageBubble = ({ 
  message, 
  isHistoricalMessage, 
  playMessageAudio, 
  isPlayingAudio,
  currentStepId,
  competenceData,
  onSaveSuccess,
  onNotesSaved,
  updateStepNoteSection, 
  getStepNotes,
  setInputText  // ✨ NOUVEAU : Pour remplir le champ avec les questions connexes
}) => {
  const [showSaveNotification, setShowSaveNotification] = useState(false);

  const handleSaveSuccess = (saveInfo) => {
    setShowSaveNotification(true);
    setTimeout(() => setShowSaveNotification(false), 3000);
    
    if (onSaveSuccess) {
      onSaveSuccess(saveInfo);
    }
    
    if (onNotesSaved && currentStepId) {
      onNotesSaved(currentStepId, saveInfo.rideau);
    }
  };
 
  return (
    <div
      className={`flex items-start gap-3 md:gap-4 mb-6 md:mb-8 ${
        message.role === 'assistant' ? 'justify-start' : 'justify-end'
      } ${isHistoricalMessage ? 'opacity-75' : ''}`}
    >
      {message.role === 'assistant' && (
        <div className={`w-8 h-8 md:w-12 md:h-12 rounded-full bg-gradient-to-br border-2 flex items-center justify-center flex-shrink-0 shadow-md ${
          isHistoricalMessage 
            ? 'from-purple-50 to-purple-100 border-purple-200' 
            : 'from-blue-50 to-indigo-50 border-blue-200'
        }`}>
          <Bot size={16} className={`${isHistoricalMessage ? 'text-purple-600' : 'text-blue-600'} md:w-5 md:h-5`} />
        </div>
      )}
      
      <div
        className={`max-w-[85%] md:max-w-[80%] p-3 md:p-5 rounded-2xl shadow-sm relative ${
          message.role === 'assistant'
            ? message.isError 
              ? 'bg-gradient-to-br from-red-50 to-pink-50 border border-red-200'
              : isHistoricalMessage
              ? 'bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200'
              : 'bg-white border border-gray-200 shadow-md'
            : isHistoricalMessage
            ? 'bg-gradient-to-br from-purple-100 to-purple-200 border border-purple-300 text-gray-800'
            : 'bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 text-gray-800'
        }`}
      >
        {showSaveNotification && (
          <div className="absolute -top-10 right-0 bg-green-500 text-white px-3 py-2 rounded-lg text-sm shadow-lg animate-slide-in-right z-10">
            <div className="flex items-center gap-2">
              <CheckCircle size={14} />
              <span>Sauvegardé dans les notes</span>
            </div>
          </div>
        )}

        <div className="max-w-none">
          <EnhancedChatMarkdown 
            content={message.content} 
            isAssistant={message.role === 'assistant'}
            className="text-gray-700 text-sm md:text-base leading-relaxed"
            citations={message.context?.citations || []}
            messageId={message.id}
            onCitationClick={(citationNumber) => {
              console.log('📖 Citation cliquée:', citationNumber);
              // Le scroll est géré automatiquement dans ChatMarkdown
            }}
          />
        </div>
        
        {/* ✨ NOUVEAU : Afficher SourcesPanel si des citations sont disponibles */}
        {message.role === 'assistant' && message.context?.citations?.length > 0 && (
          <SourcesPanel 
            messageContext={{
              metadata: {
                toolUsed: message.context.toolUsed || 'perplexity_sonar',
                processingTime: message.context.processingTime || 0,
                sources: message.context.citations.map(citation => ({
                  url: citation.url,
                  title: citation.title,
                  snippet: citation.snippet || ''
                })),
                modelUsed: message.context.model || 'sonar',
                searchEngine: 'perplexity_sonar'
              }
            }}
            messageId={message.id}
            isOpen={true}
          />
        )}
        
        {/* ✨ NOUVEAU : Afficher RelatedQuestionsPanel si des questions connexes sont disponibles */}
        {message.role === 'assistant' && message.context?.relatedQuestions?.length > 0 && (
          <RelatedQuestionsPanel
            questions={message.context.relatedQuestions}
            onQuestionClick={(question) => {
              // Remplir le champ de saisie avec la question
              if (typeof setInputText === 'function') {
                setInputText(question);
              }
            }}
            isLoading={false}
          />
        )}
        
        <div className={`text-xs md:text-sm mt-3 md:mt-4 flex items-center justify-between ${
          message.role === 'assistant' 
            ? isHistoricalMessage ? 'text-purple-500' : 'text-gray-500'
            : isHistoricalMessage ? 'text-purple-700' : 'text-purple-600'
        }`}>
          <span>{new Date(message.timestamp).toLocaleTimeString()}</span>
          <div className="flex items-center gap-2 md:gap-3">
            {message.context?.sourcesCount && (
              <span className="flex items-center gap-1" title={`${message.context.sourcesCount} sources utilisées`}>
                <Globe size={12} className="md:w-3.5 md:h-3.5" />
                {message.context.sourcesCount}
              </span>
            )}
            {message.context?.currentStepTitle && (
              <span className="flex items-center gap-1" title={`Étape : ${message.context.currentStepTitle}`}>
                <Target size={12} className="md:w-3.5 md:h-3.5" />
                Étape
              </span>
            )}
            
            {message.role === 'assistant' && !isHistoricalMessage && !message.isError && currentStepId && competenceData?.id && (
              <SaveToKnowledgeBase
                messageContent={message.content}
                messageContext={message.context}
                currentStepId={currentStepId}
                competenceId={competenceData?.id}
                onSaveSuccess={handleSaveSuccess}
                onSaveError={(error) => console.error('Erreur sauvegarde:', error)}
              />
            )}
            
            {message.role === 'assistant' && !isHistoricalMessage && (
              <button
                onClick={() => playMessageAudio(message.content)}
                className={`p-1 md:p-2 hover:bg-gray-100 rounded-full transition-colors duration-200 ${
                  isPlayingAudio ? 'text-blue-600' : 'text-gray-500'
                }`}
                title={isPlayingAudio ? 'Arrêter l\'audio' : 'Écouter le message'}
              >
                {isPlayingAudio ? <VolumeX size={12} className="md:w-4 md:h-4" /> : <Volume2 size={12} className="md:w-4 md:h-4" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {message.role === 'user' && (
        <div className={`w-8 h-8 md:w-12 md:h-12 rounded-full bg-gradient-to-br border-2 flex items-center justify-center flex-shrink-0 shadow-md ${
          isHistoricalMessage
            ? 'from-purple-100 to-purple-200 border-purple-300'
            : 'from-purple-100 to-pink-100 border-purple-200'
        }`}>
          <User size={16} className={`${isHistoricalMessage ? 'text-purple-700' : 'text-purple-600'} md:w-5 md:h-5`} />
        </div>
      )}
    </div>
  );
};

// Header contextuel responsive
const ContextInfo = ({ 
  userObjective, 
  currentStep, 
  userProgress, 
  showMobileInfo, 
  setShowMobileInfo,
  lastSavedAt,
  showHistory,
  setShowHistory,
  historicalMessagesCount,
  onStartFreshConversation,
  onToggleFullscreen,
  isMaximized,
  isFullscreen,
  hasChatMessages,
  selectedTool,
  onToolChange,
  sourcesCount,
  isLoading
}) => (
  <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
    <div className="hidden md:block p-4 bg-gradient-to-r from-blue-50 to-indigo-50">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <span className="font-medium">Objectif:</span>
            <span className="truncate flex-1">{userObjective || 'Non défini'}</span>
          </div>
          
          {currentStep && (
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-orange-600 flex-shrink-0" />
              <span className="font-medium">Étape:</span>
              <span className="truncate flex-1">{currentStep.titre}</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {historicalMessagesCount > 0 && (
            <button 
              onClick={() => setShowHistory(!showHistory)}
              className={`p-2 bg-white hover:bg-gray-50 rounded-full transition-colors duration-200 shadow-md relative ${
                showHistory ? 'bg-purple-50 ring-2 ring-purple-300' : ''
              }`}
              title={showHistory ? 'Masquer l\'historique' : `Afficher l'historique (${historicalMessagesCount} messages)`}
            >
              {showHistory ? <EyeOff size={16} className="text-purple-600" /> : <History size={16} className="text-purple-600" />}
              {!showHistory && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {historicalMessagesCount > 99 ? '99+' : historicalMessagesCount}
                </div>
              )}
            </button>
          )}
          
          {(hasChatMessages || showHistory) && (
            <button 
              onClick={onStartFreshConversation}
              className="p-2 bg-white hover:bg-gray-50 rounded-full transition-colors duration-200 shadow-md"
              title="Démarrer une nouvelle conversation"
            >
              <Sparkles size={16} className="text-blue-600" />
            </button>
          )}
          
          <button 
            onClick={onToggleFullscreen}
            className="p-2 bg-white hover:bg-gray-50 rounded-full transition-colors duration-200 shadow-md"
            title={isFullscreen || isMaximized ? "Quitter le plein écran" : "Mode plein écran"}
          >
            {isMaximized || isFullscreen ? (
              <Minimize2 size={16} className="text-blue-600" />
            ) : (
              <Maximize2 size={16} className="text-blue-600" />
            )}
          </button>
          
          {/* ✨ NOUVEAU : Sélecteur d'outil */}
          <ToolModeDropdown
            selectedTool={selectedTool}
            onToolChange={onToolChange}
            sourcesCount={sourcesCount}
            disabled={isLoading}
          />
        </div>
      </div>
    </div>

    <div className="md:hidden">
      <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-between">
          
          <div className="flex items-center gap-2">
            {/* ✨ NOUVEAU : ToolModeDropdown pour mobile */}
            <div className="mr-1">
              <ToolModeDropdown
                selectedTool={selectedTool}
                onToolChange={onToolChange}
                sourcesCount={sourcesCount}
                disabled={isLoading}
                compactMode={true}
              />
            </div>
            
            {historicalMessagesCount > 0 && (
              <button 
                onClick={() => setShowHistory(!showHistory)}
                className={`p-1 rounded-full transition-colors duration-200 relative ${
                  showHistory ? 'bg-purple-100 ring-2 ring-purple-300' : 'bg-white hover:bg-gray-50'
                }`}
                title={showHistory ? 'Masquer l\'historique' : `Afficher l'historique`}
              >
                {showHistory ? <EyeOff size={14} className="text-purple-600" /> : <History size={14} className="text-purple-600" />}
                {!showHistory && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                    {historicalMessagesCount > 99 ? '99+' : historicalMessagesCount}
                  </div>
                )}
              </button>
            )}
            
            {(hasChatMessages || showHistory) && (
              <button 
                onClick={onStartFreshConversation}
                className="p-1 bg-white hover:bg-gray-50 rounded-full transition-colors duration-200"
                title="Nouvelle conversation"
              >
                <Sparkles size={14} className="text-blue-600" />
              </button>
            )}
            
            <button 
              onClick={onToggleFullscreen}
              className="p-1 bg-white hover:bg-gray-50 rounded-full transition-colors duration-200"
              title="Plein écran"
            >
              {isMaximized || isFullscreen ? (
                <Minimize2 size={14} className="text-blue-600" />
              ) : (
                <Maximize2 size={14} className="text-blue-600" />
              )}
            </button>

            <button
              onClick={() => setShowMobileInfo(!showMobileInfo)}
              className="p-1 hover:bg-white rounded-full transition-colors"
            >
              {showMobileInfo ? 
                <ChevronUp className="h-4 w-4 text-blue-600" /> : 
                <ChevronDown className="h-4 w-4 text-blue-600" />
              }
            </button>
          </div>
        </div>
      </div>
      
      {showMobileInfo && (
        <div className="p-3 bg-white border-b border-gray-200 space-y-3">
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <Target className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-gray-600 mb-1">Objectif</div>
                <div className="text-sm text-gray-800 leading-tight">{userObjective || 'Non défini'}</div>
              </div>
            </div>
            
            {currentStep && (
              <div className="flex items-start gap-2">
                <Zap className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-gray-600 mb-1">Étape courante</div>
                  <div className="text-sm text-gray-800 leading-tight">{currentStep.titre}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </div>
);

// COMPOSANT PRINCIPAL
const ChatInterface = ({ 
  userObjective, 
  searchObjective, 
  savedSources, 
  actionPlan, 
  currentStepId,
  competenceData,
  changeCurrentStep,
  onUpdateStepResults,
  userProgress,
  chatContextManager,
  disableModals = false,
  updateStepNoteSection,
  getStepNotes,
  saveStepSynthesis,
  getStepSynthesis,
  onNavigateToNotes,
  onNotesSaved,
  onChatModeChange  // ✨ NOUVEAU : Callback pour notifier le parent du changement de mode
}) => {
  
  // ✨ NOUVEAU : États pour le sélecteur d'outil et les recommandations (DÉCLARÉS EN PREMIER)
  const [selectedTool, setSelectedTool] = useState('auto');
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [currentRecommendations, setCurrentRecommendations] = useState([]);
  
  const [inputText, setInputText] = useState('');
  const [isMaximized, setIsMaximized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const [showMobileInfo, setShowMobileInfo] = useState(false);
  
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [currentAudioUrl, setCurrentAudioUrl] = useState(null);
  
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [saveNotification, setSaveNotification] = useState('');
  
  const [isListening, setIsListening] = useState(false);
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition
  } = useSpeechRecognition();

  const chatConversation = useChatConversation({
    userObjective,
    searchObjective,
    savedSources,
    actionPlan,
    currentStepId,
    competenceData,
    chatContextManager,
    onUpdateStepResults,
    selectedTool  // ✨ NOUVEAU : Mode de traitement sélectionné
  });

  const chatGuided = useChatGuided({
    userObjective,
    currentStepId,
    savedSources,
    actionPlan,
    userProgress,
    updateStepNoteSection,
    getStepNotes,
    saveStepSynthesis,
    getStepSynthesis
  }, chatConversation);

  // ✨ NOUVEAU : Notifier le parent quand le mode chat change
  useEffect(() => {
    if (onChatModeChange && chatGuided.chatMode) {
      onChatModeChange(chatGuided.chatMode);
    }
  }, [chatGuided.chatMode, onChatModeChange]);

  const chatContainerRef = useRef(null);
  const fullscreenElementRef = useRef(null);
  const audioRef = useRef(null);
  
  useEffect(() => {
    if (transcript) {
      setInputText(transcript);
    }
  }, [transcript]);

  useEffect(() => {
    setIsListening(listening);
  }, [listening]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (chatContainerRef.current) {
      const container = chatContainerRef.current;
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      
      if (isNearBottom) {
        setTimeout(() => {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          });
        }, 100);
      }
    }
  }, [chatConversation.chatMessages, chatConversation.isLoading]);

  useEffect(() => {
    return () => {
      if (currentAudioUrl) {
        URL.revokeObjectURL(currentAudioUrl);
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [currentAudioUrl]);

  const handleSendMessage = useCallback(async () => {
    if (!inputText.trim()) return;
    
    const success = await chatConversation.sendMessage(inputText.trim());
    if (success) {
      setInputText('');
      resetTranscript();
    }
  }, [inputText, chatConversation, resetTranscript]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  const toggleListening = useCallback(() => {
    if (!browserSupportsSpeechRecognition) {
      chatConversation.setError("Votre navigateur ne supporte pas la reconnaissance vocale.");
      return;
    }

    if (isListening) {
      SpeechRecognition.stopListening();
    } else {
      SpeechRecognition.startListening({ 
        language: 'fr-FR',
        continuous: false 
      });
    }
  }, [isListening, browserSupportsSpeechRecognition, chatConversation]);

  const playMessageAudio = useCallback(async (messageContent) => {
    const currentUser = auth.currentUser;
    if (!currentUser?.uid) {
      chatConversation.setError('Vous devez être connecté pour utiliser l\'audio.');
      return;
    }

    if (isPlayingAudio) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlayingAudio(false);
      if (currentAudioUrl) {
        URL.revokeObjectURL(currentAudioUrl);
        setCurrentAudioUrl(null);
      }
      return;
    }

    try {
      setIsPlayingAudio(true);
      chatConversation.setError('');
      
      const audioUrl = await chatContextManager.generateAudio(messageContent);
      
      if (audioUrl) {
        setCurrentAudioUrl(audioUrl);
        
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        
        audio.onended = () => {
          setIsPlayingAudio(false);
          setCurrentAudioUrl(null);
        };
        
        audio.onerror = () => {
          setIsPlayingAudio(false);
          setCurrentAudioUrl(null);
          chatConversation.setError('Erreur lors de la lecture audio');
        };
        
        await audio.play();
      }
    } catch (error) {
      setIsPlayingAudio(false);
      chatConversation.setError(error.message || 'Erreur lors de la génération audio.');
    }
  }, [isPlayingAudio, currentAudioUrl, chatContextManager, chatConversation]);

  const toggleFullscreen = async () => {
    const element = fullscreenElementRef.current;
    if (!element) return;

    try {
      if (isFullscreen || isMaximized) {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        }
        setIsMaximized(false);
      } else {
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        }
        setIsMaximized(true);
      }
    } catch (error) {
      console.error('Erreur fullscreen:', error);
      setIsMaximized(!isMaximized);
    }
  };

  const handleSaveSuccess = useCallback((saveInfo) => {
    setSaveNotification(`Sauvegardé dans "${saveInfo.rideauLabel}"`);
    setTimeout(() => setSaveNotification(''), 3000);
    
    if (onNotesSaved && currentStepId) {
      onNotesSaved(currentStepId, saveInfo.rideau);
    }
  }, [onNotesSaved, currentStepId]);

  // 🔥 NOUVEAU : Wrapper pour startFreshConversation
  const handleStartFreshConversation = useCallback(() => {
    if (chatConversation?.startFreshConversation) {
      chatConversation.startFreshConversation();
    }
    // Masquer l'historique pour donner un feedback visuel du nouveau départ
    setShowHistory(false);
  }, [chatConversation?.startFreshConversation]);

  const getPlaceholder = () => {
    if (chatGuided.chatMode === 'guided' && chatGuided.currentQuestion) {
      return "Sélectionne une ou plusieurs options";
    }
    if (chatGuided.chatMode === 'guided') {
      return "Mode questions guidées - sélection multiple possible";
    }
    if (currentStepId && actionPlan?.steps?.find(s => s.id === currentStepId)) {
      const currentStep = actionPlan.steps.find(s => s.id === currentStepId);
      return `Parlons de cette étape : "${currentStep.titre}"...`;
    }
    if (userObjective) {
      return "Parlons de votre objectif...";
    }
    return chatConversation.hasMessages 
      ? "Continuons la conversation avec Aladinn..." 
      : "Commençons une nouvelle conversation avec Aladinn...";
  };

  if (!chatContextManager) {
    return (
      <div className="p-4 text-red-600 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle size={20} />
          <span className="font-semibold">Erreur de configuration</span>
        </div>
        <p>ChatContextManager non disponible. Vérifiez la configuration.</p>
      </div>
    );
  }

  const currentStep = actionPlan?.steps?.find(s => s.id === currentStepId);

  const shouldShowChatMessages = () => {
    return chatConversation.chatMessages;
  };

  const displayMessages = shouldShowChatMessages();
  const hasVisibleMessages = displayMessages.length > 0;
  
  return (
    <div className={`w-full flex flex-col ${isMaximized ? 'h-screen' : 'h-full'}`}>
      <style>{fontStyles}</style>
      
      <div 
        ref={fullscreenElementRef}
        className={`flex flex-col bg-white shadow-lg border border-gray-200 transition-all duration-300 ease-in-out
          ${isMaximized 
            ? 'fixed inset-0 z-50 rounded-none h-screen w-screen' 
            : 'rounded-xl h-full min-h-[600px]'
          }`}
      >
        <ContextInfo 
          userObjective={userObjective}
          currentStep={currentStep}
          userProgress={chatGuided.userProgress}
          showMobileInfo={showMobileInfo}
          setShowMobileInfo={setShowMobileInfo}
          lastSavedAt={chatConversation.lastSavedAt}
          showHistory={showHistory}
          setShowHistory={setShowHistory}
          historicalMessagesCount={chatConversation.totalMessageCount}
          onStartFreshConversation={handleStartFreshConversation}
          onToggleFullscreen={toggleFullscreen}
          isMaximized={isMaximized}
          isFullscreen={isFullscreen}
          hasChatMessages={chatConversation.chatMessages.length > 0}
          selectedTool={selectedTool}
          onToolChange={setSelectedTool}
          sourcesCount={savedSources?.length || 0}
          isLoading={chatConversation.isLoading}
        />

        <div 
          ref={chatContainerRef}
          className={`flex-1 overflow-y-auto bg-gradient-to-b from-gray-50 to-white relative`}
          style={{ 
            minHeight: isMaximized ? 'calc(100vh - 200px)' : '400px',
            scrollBehavior: 'smooth',
            overscrollBehavior: 'contain'
          }}
        >
          {/* Module d'inspiration complet (Recherche, Sources, Synthèse) */}
          {selectedTool === 'source_analysis' && (
            <div className="h-full">
              <InspirationChatView
                competenceData={competenceData}
                userObjective={userObjective}
                onExitInspirationMode={() => setSelectedTool('auto')}
              />
            </div>
          )}

          {/* Base de connaissances intégrée (Option B) */}
          {selectedTool === 'knowledge_base' && (
            <div className="px-3 md:px-4 pt-3 md:pt-4">
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <KnowledgeBase
                  userId={auth.currentUser?.uid || ''}
                  competence={competenceData}
                  initialViewMode="documents"
                  embedded={true}
                  showHeader={false}
                />
              </div>
            </div>
          )}
          {feedbackMessage && (
            <div className="p-3 md:p-4">
              <FeedbackMessage
                message={feedbackMessage}
                onClose={() => setFeedbackMessage('')}
                type="success"
              />
            </div>
          )}

          {chatGuided.feedbackMessage && (
            <div className="p-3 md:p-4">
              <FeedbackMessage
                message={chatGuided.feedbackMessage}
                onClose={() => chatGuided.setFeedbackMessage('')}
                type="success"
              />
            </div>
          )}

          {saveNotification && (
            <div className="p-3 md:p-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                <span className="text-sm text-green-800 font-medium">{saveNotification}</span>
              </div>
            </div>
          )}

          {/* Ancien panel de sélection de mode supprimé - remplacé par le toggle en bas */}

          {/* Séparateur si on a de l'historique et qu'il est affiché */}
          {selectedTool !== 'source_analysis' && showHistory && chatConversation.hasHistoricalMessages && (
            <div className="relative my-4 md:my-6 px-3 md:px-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-purple-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 md:px-4 py-1 bg-purple-50 text-purple-700 rounded-full border border-purple-200 font-medium">
                  Historique des conversations
                </span>
              </div>
            </div>
          )}

          {/* Messages (historique + courants si demandé, sinon seulement courants) */}
          {selectedTool !== 'source_analysis' && (hasVisibleMessages || showHistory) && (
            <div className="px-3 md:px-4 pb-3 md:pb-4">
              {(showHistory ? [...chatConversation.historicalMessages, ...displayMessages] : displayMessages).map((message, index) => {
                const isHistoricalMessage = showHistory && index < chatConversation.historicalMessages.length;
                
                return (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    isHistoricalMessage={isHistoricalMessage}
                    playMessageAudio={playMessageAudio}
                    isPlayingAudio={isPlayingAudio}
                    currentStepId={currentStepId}
                    competenceData={competenceData}
                    onSaveSuccess={handleSaveSuccess}
                    onNotesSaved={onNotesSaved}
                    updateStepNoteSection={updateStepNoteSection}
                    getStepNotes={getStepNotes}
                    setInputText={setInputText}
                  />
                );
              })}
            </div>
          )}

          {/* Panel de questions guidées ou champ de saisie selon le mode */}
          {selectedTool !== 'source_analysis' && !hasVisibleMessages && !showHistory && (
            <div className="px-3 md:px-4">
              {chatGuided.isGuidedMode ? (
                <div className="mt-3 md:mt-4">
                  <ContextualQuestionsPanel
                    userObjective={userObjective}
                    currentStep={chatGuided.contextualData.currentStep}
                    savedSources={savedSources}
                    actionPlan={actionPlan}
                    chatCore={chatConversation}
                    guidedState={chatGuided}
                    onNavigateToNotes={onNavigateToNotes}
                  />
                </div>
              ) : (
                <div className="w-full mt-3 md:mt-4">
                  <ChatInputField
                    inputText={inputText}
                    setInputText={setInputText}
                    handleSendMessage={handleSendMessage}
                    handleKeyPress={handleKeyPress}
                    toggleListening={toggleListening}
                    isListening={isListening}
                    isLoading={chatConversation.isLoading}
                    browserSupportsSpeechRecognition={browserSupportsSpeechRecognition}
                    placeholder={getPlaceholder()}
                    chatMode={chatGuided.chatMode}
                  />
                </div>
              )}
            </div>
          )}

          {selectedTool !== 'source_analysis' && chatGuided.isGuidedMode && hasVisibleMessages && (
            <div className="p-3 md:p-4">
              <ContextualQuestionsPanel
                userObjective={userObjective}
                currentStep={chatGuided.contextualData.currentStep}
                savedSources={savedSources}
                actionPlan={actionPlan}
                chatCore={chatConversation}
                guidedState={chatGuided}
                onNavigateToNotes={onNavigateToNotes}
              />
            </div>
          )}

          {selectedTool !== 'source_analysis' && chatConversation.isLoading && (
            <div className="px-3 md:px-4 pb-3 md:pb-4">
              <div className="flex items-start gap-3 md:gap-4 mb-6 md:mb-8">
                <div className="w-8 h-8 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 flex items-center justify-center shadow-md">
                  <Bot size={16} className="text-blue-600 md:w-5 md:h-5" />
                </div>
                <div className="bg-white border border-gray-200 shadow-md p-3 md:p-5 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-blue-500 animate-bounce"></div>
                      <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-blue-500 animate-bounce delay-100"></div>
                      <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-blue-500 animate-bounce delay-200"></div>
                    </div>
                    <span className="text-gray-600 text-sm md:text-base">
                      {chatGuided.isGuidedMode 
                        ? chatGuided.currentQuestion 
                          ? 'Traitement de tes sélections...' 
                          : 'Aladinn prépare une question contextuelle...'
                        : 'Aladinn réfléchit...'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Toggle pour basculer entre Conversation et Avancement - TOUJOURS VISIBLE APRÈS LE CHAMP DE SAISIE */}
        {/* Masqué quand le module d'inspiration est actif */}
        {selectedTool !== 'source_analysis' && (
          <div className={`bg-white border-t border-gray-200 flex-shrink-0 sticky bottom-0 ${
            isMaximized ? 'rounded-none' : 'rounded-b-xl'
          }`}>
            {hasVisibleMessages && chatGuided.chatMode === 'conversation' && (
              <div className="p-3 md:p-4">
                <ChatInputField
                  inputText={inputText}
                  setInputText={setInputText}
                  handleSendMessage={handleSendMessage}
                  handleKeyPress={handleKeyPress}
                  toggleListening={toggleListening}
                  isListening={isListening}
                  isLoading={chatConversation.isLoading}
                  browserSupportsSpeechRecognition={browserSupportsSpeechRecognition}
                  placeholder={getPlaceholder()}
                  chatMode={chatGuided.chatMode}
                />
              </div>
            )}

            <GuidedModeToggle
              currentMode={chatGuided.chatMode}
              onModeChange={chatGuided.changeMode}
              disabled={chatConversation.isLoading}
            />
          </div>
        )}
      </div>

      {chatConversation.error && (
        <div className="fixed top-6 right-6 bg-gradient-to-br from-red-50 to-pink-50 border border-red-200 rounded-xl shadow-lg p-4 md:p-5 max-w-xs md:max-w-sm z-50">
          <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
            <AlertCircle size={16} className="text-red-600 md:w-5 md:h-5" />
            <span className="font-semibold text-red-800 text-sm md:text-base">Erreur</span>
          </div>
          <p className="text-red-700 text-sm md:text-base">{chatConversation.error}</p>
          <button 
            onClick={() => chatConversation.setError(null)}
            className="mt-2 md:mt-3 text-red-600 hover:text-red-800 text-xs md:text-sm underline"
          >
            Fermer
          </button>
        </div>
      )}

      {chatGuided.errorMessage && (
        <div className="fixed top-6 left-6 bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200 rounded-xl shadow-lg p-4 md:p-5 max-w-xs md:max-w-sm z-50">
          <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
            <HelpCircle size={16} className="text-orange-600 md:w-5 md:h-5" />
            <span className="font-semibold text-orange-800 text-sm md:text-base">Mode guidé</span>
          </div>
          <p className="text-orange-700 text-sm md:text-base">{chatGuided.errorMessage}</p>
          <button 
            onClick={() => chatGuided.setErrorMessage('')}
            className="mt-2 md:mt-3 text-orange-600 hover:text-orange-800 text-xs md:text-sm underline"
          >
            Fermer
          </button>
        </div>
      )}

      {chatGuided.isGuidedMode && chatGuided.selectedOptions && chatGuided.selectedOptions.length > 0 && (
        <div className="fixed bottom-4 md:bottom-6 left-4 md:left-6 z-40">
          <div className="bg-green-500 text-white px-3 md:px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
            <CheckCircle size={14} className="md:w-4 md:h-4" />
            <span className="text-xs md:text-sm font-medium">
              {chatGuided.selectedOptions.length} option{chatGuided.selectedOptions.length > 1 ? 's' : ''} sélectionnée{chatGuided.selectedOptions.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInterface;
